import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { Button as RACButton } from "react-aria-components";

import type { BrandSelectOption as Brand } from "@/features/brands/shared/hooks/useGetBrandsByProjectId";

import { Avatar } from "@/components/avatar";
import { Button } from "@/components/button";
import { Checkbox, CheckboxGroup } from "@/components/checkbox";
import { Dialog, DialogTrigger } from "@/components/dialog";
import { Popover } from "@/components/popover";
import { SearchField, SearchInput } from "@/components/search-field";
import { useGetBrandsByProjectId } from "@/features/brands/shared/hooks/useGetBrandsByProjectId";
import { useGetCurrentProjectId } from "@/hooks/useGetCurrentProjectId";

const MAX_STACKED_AVATARS = 3;

// No brand selected means no filter, so it reads as "All brands" like selecting every brand does.
export function BrandFilter({
  brandIds,
  onChange,
}: {
  brandIds: number[];
  onChange: (brandIds: number[]) => void;
}) {
  const projectId = useGetCurrentProjectId();
  const { data: brands = [] } = useGetBrandsByProjectId(projectId);
  const [search, setSearch] = useState("");
  const selectedBrands = brands.filter((brand) => brandIds.includes(brand.id));
  const visibleBrands = brands.filter((brand) =>
    brand.name.toLowerCase().includes(search.trim().toLowerCase()),
  );

  return (
    <DialogTrigger onOpenChange={(isOpen) => !isOpen && setSearch("")}>
      <RACButton className="flex min-w-[208px] cursor-pointer items-center gap-sm rounded-md border border-border-primary bg-bg-primary px-[14px] py-[10px] text-sm font-semibold text-text-primary outline-hidden focus-visible:ring-2 focus-visible:ring-border-brand">
        <TriggerLabel brands={brands} selectedBrands={selectedBrands} />
        <ChevronDown className="ml-auto size-5 shrink-0 text-fg-quaternary" />
      </RACButton>
      <Popover className="flex w-72 flex-col" placement="bottom end">
        <Dialog aria-label="Filter by brand" className="flex flex-col">
          <div className="border-b border-border-secondary p-md">
            <SearchField
              aria-label="Search brands"
              autoFocus
              onChange={setSearch}
              value={search}
            >
              <SearchInput
                onClear={() => setSearch("")}
                placeholder="Search brands"
              />
            </SearchField>
          </div>
          <CheckboxGroup
            aria-label="Brands"
            className="max-h-72 overflow-y-auto p-sm"
            onChange={(ids) => onChange(ids.map(Number))}
            value={brandIds.map(String)}
          >
            {visibleBrands.map((brand) => (
              <Checkbox
                className="rounded-sm px-sm py-sm hover:bg-bg-secondary"
                key={brand.id}
                value={String(brand.id)}
              >
                <span className="flex min-w-0 items-center gap-sm">
                  <BrandAvatar
                    className="size-5 shrink-0"
                    name={brand.name}
                    profilePicture={brand.profilePicture}
                  />
                  <span className="truncate">{brand.name}</span>
                </span>
              </Checkbox>
            ))}
            {visibleBrands.length === 0 && (
              <p className="p-md text-center text-sm text-text-tertiary">
                No brands match &ldquo;{search.trim()}&rdquo;
              </p>
            )}
          </CheckboxGroup>
          <div className="flex items-center justify-between gap-sm border-t border-border-secondary bg-bg-secondary px-md py-sm">
            <span className="text-xs font-medium text-text-tertiary">
              {selectedBrands.length} of {brands.length} selected
            </span>
            <div className="flex gap-xs">
              <Button
                color="gray"
                onPress={() =>
                  onChange([
                    ...new Set([
                      ...brandIds,
                      ...visibleBrands.map((brand) => brand.id),
                    ]),
                  ])
                }
                size="sm"
                variant="tertiary"
              >
                Select all
              </Button>
              <Button
                color="gray"
                isDisabled={brandIds.length === 0}
                onPress={() => onChange([])}
                size="sm"
                variant="tertiary"
              >
                Clear
              </Button>
            </div>
          </div>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}

function TriggerLabel({
  brands,
  selectedBrands,
}: {
  brands: Brand[];
  selectedBrands: Brand[];
}) {
  const isAll =
    selectedBrands.length === 0 || selectedBrands.length === brands.length;
  const shown = isAll ? brands : selectedBrands;
  const hiddenCount = shown.length - MAX_STACKED_AVATARS;
  const label = isAll
    ? "All brands"
    : selectedBrands.length === 1
      ? selectedBrands[0]?.name
      : `${selectedBrands.length} brands`;

  return (
    <span className="flex min-w-0 items-center gap-sm">
      <span className="flex -space-x-1">
        {shown.slice(0, MAX_STACKED_AVATARS).map((brand) => (
          <BrandAvatar
            className="size-5 ring-2 ring-bg-primary"
            key={brand.id}
            name={brand.name}
            profilePicture={brand.profilePicture}
          />
        ))}
        {hiddenCount > 0 && (
          <span className="flex size-5 items-center justify-center rounded-full bg-bg-secondary text-[10px] font-medium text-text-secondary ring-2 ring-bg-primary">
            +{hiddenCount}
          </span>
        )}
      </span>
      <span className="truncate">{label}</span>
    </span>
  );
}

export function BrandAvatar({
  className,
  name,
  profilePicture,
}: {
  className?: string;
  name: string;
  profilePicture: null | string;
}) {
  return (
    <Avatar
      alt={name}
      className={`rounded-full ${className ?? ""}`}
      initials={name.slice(0, 2).toUpperCase()}
      src={profilePicture ?? undefined}
    />
  );
}
